import { Box, Button, Card, Collapse, Grid, Menu, SegmentedControl } from '@mantine/core';

import { IconChevronDown, IconChevronRight, IconX } from '@tabler/icons-react';

import { resolveAvailableFilters, resolveFilter } from '../../helpers/ruleTemplates';
import AudienceRuleFilter from './AudienceRuleFilter';

const AudienceRule = ({
  icon,
  index,
  rule,
  updateRule,
  removeRule,
  toggle,
  open,
  readOnly,
  filters,
  availableRules,
}) => {
  if (rule === undefined) {
    return '';
  }

  const ruleAvailableFilters = resolveAvailableFilters(rule, availableRules);

  const addFilter = (filterKey) => {
    const modifiedRule = { ...rule };
    modifiedRule.filters = [...rule.filters];
    modifiedRule.filters.push(Object.assign({}, filters[filterKey], ruleAvailableFilters[filterKey], { values: [] }));
    updateRule(modifiedRule);
  };

  const removeFilter = (index) => {
    const modifiedRule = { ...rule };
    modifiedRule.filters = [...modifiedRule.filters];
    modifiedRule.filters.splice(index, 1);
    updateRule(modifiedRule);
  };

  const updateFilter = (index, filter) => {
    const modifiedRule = { ...rule };
    modifiedRule.filters[index] = filter;
    updateRule(modifiedRule);
  };

  const filterElements = rule.filters.map((filter, index) => (
    <div key={index} style={{ color: 'var(--mantine-color-dimmed)' }}>
      <AudienceRuleFilter
        readOnly={readOnly}
        filter={resolveFilter(filter, filters, ruleAvailableFilters)}
        updateFilter={(filter) => updateFilter(index, filter)}
        removeFilter={() => removeFilter(index)}
      />
    </div>
  ));

  const availableFilterElements = [];
  for (const filterKey of Object.keys(ruleAvailableFilters)) {
    availableFilterElements.push(
      <Menu.Item key={filterKey} onClick={() => addFilter(filterKey)}>
        {filters[filterKey].name}
      </Menu.Item>
    );
  }

  return (
    <Card withBorder>
      <Card.Section inheritPadding py="sm">
        <Box component="h5" display="flex" style={{ justifyContent: 'space-between', alignItems: 'center', margin: 0 }}>
          <Box>
            {icon}&nbsp;
            <Button aria-controls={`collapse-${index}`} aria-expanded={open} variant="subtle" onClick={toggle}>
              <Box display="inline-block">
                {rule.scope === 'google' ? (rule.type === 'url__' ? 'Page URL ' : 'Referrer URL ') : ''}
                {rule.inverseSelection ? `Not ${rule.name}` : rule.name}&nbsp;
              </Box>
              {open ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
            </Button>
          </Box>
          <Box>
            <Button disabled={readOnly} variant="subtle" onClick={removeRule}>
              <IconX size={14} />
            </Button>
          </Box>
        </Box>

        <Collapse expanded={open}>
          <div id={`collapse-${index}`}>
            {rule.scope === 'google' && (
              <>
                <hr />
                <Grid data-tour="segmentOperandSelect">
                  <Grid.Col span={{ base: 12, sm: 3 }}>Operation</Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 9 }}>
                    <SegmentedControl
                      disabled={readOnly}
                      size="sm"
                      value={rule.operand ?? ''}
                      onChange={(operand) => updateRule(Object.assign({}, rule, { operand }))}
                      data={[
                        { label: 'Include Visitors', value: 'include' },
                        { label: 'Exclude Visitors', value: 'exclude' },
                      ]}
                    />
                  </Grid.Col>
                </Grid>
                <hr />
                <Grid data-tour="segmentTypeSelect">
                  <Grid.Col span={{ base: 12, sm: 3 }}>URL</Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 9 }}>
                    <SegmentedControl
                      disabled={readOnly}
                      size="sm"
                      value={rule.type ?? ''}
                      onChange={(type) => updateRule(Object.assign({}, rule, { type }))}
                      data={[
                        { label: 'Page URL', value: 'url__' },
                        { label: 'Referrer URL', value: 'ref_url__' },
                      ]}
                    />
                  </Grid.Col>
                </Grid>
              </>
            )}
            <hr />
            <Grid data-tour="segmentModeSelect">
              <Grid.Col span={{ base: 12, sm: 3 }}>Mode</Grid.Col>
              <Grid.Col span={{ base: 12, sm: 9 }}>
                <SegmentedControl
                  disabled={readOnly}
                  size="sm"
                  value={String(rule.inverseSelection)}
                  onChange={(value) => updateRule(Object.assign({}, rule, { inverseSelection: value === 'true' }))}
                  data={[
                    { label: rule.name, value: 'false' },
                    { label: `Not ${rule.name}`, value: 'true' },
                  ]}
                />
              </Grid.Col>
            </Grid>
            <hr />
            {rule.scope !== 'google' && (
              <Grid>
                <Grid.Col span={{ base: 12, sm: 12 }}>
                  <Menu data-tour="segmentPageFilters">
                    <Menu.Target>
                      <Button disabled={readOnly} variant="default">
                        Add Filters
                      </Button>
                    </Menu.Target>
                    <Menu.Dropdown>{availableFilterElements}</Menu.Dropdown>
                  </Menu>
                </Grid.Col>
              </Grid>
            )}
            <br />
            <Grid>
              <Grid.Col span={{ base: 12, sm: 12 }} data-tour="segmentSelectedFilterElements">
                {filterElements}
              </Grid.Col>
            </Grid>
          </div>
        </Collapse>
      </Card.Section>
    </Card>
  );
};

export default AudienceRule;
